import React, { ChangeEventHandler } from 'react';
import styled from 'styled-components';
import { DesignSystemTextColor, Theme } from '@minecraft.themes';
import { FlexDynamic, FlexStyleProps } from './Containers';
import { BodyText } from './Typography';

const CheckboxWrapper = styled.label`
  display: inline-flex;
  position: relative;
  align-items: center;
  cursor: pointer;
  margin: 0;
`;

const getBackgroundColor = (theme: Theme, color: 'primary' | 'success', isLocked: boolean) => {
  const { bgColors } = theme.designSystem;
  if (color === 'success') {
    return isLocked ? bgColors.successDisabled : bgColors.successActive;
  }
  return isLocked ? bgColors.primaryDisabled : bgColors.primary;
};

const CheckboxInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

const CheckboxSlider = styled.div<{ isLocked: boolean; checked: boolean; color: 'primary' | 'success' }>`
  width: 3rem;
  height: 1.8rem;
  background-color: ${({ theme, color, isLocked, checked }) =>
    checked ? getBackgroundColor(theme, color, isLocked) : theme.designSystem.bgColors.statusDefault};
  border-radius: 625rem;
  cursor: pointer;
  position: relative;
  transition: background-color 0.3s;

  &:after {
    content: '';
    position: absolute;
    top: 0.2rem;
    background-color: ${({ theme, isLocked }) =>
      isLocked ? theme.designSystem.bgColors.toggleKnobDisabled : theme.designSystem.bgColors.toggleKnobActive};
    border: 1px solid ${({ theme }) => theme.designSystem.bgColors.toggleKnobActive};
    border-radius: 625rem;
    height: 1.4rem;
    width: 1.4rem;
    transition: all 0.3s;
    transform: translateX(${({ checked }) => (checked ? '100%' : '15%')});
  }

  ${CheckboxInput}:focus + & {
    outline: 3px solid ${({ theme }) => theme.designSystem.borderColors.inputFocus};
  }
`;

export interface ToggleCheckboxProps {
  checked: boolean;
  isLocked?: boolean;
  onChange: ChangeEventHandler<HTMLInputElement>;
  className?: string;
  toggleOnText?: string;
  toggleOffText?: string;
  children?: React.ReactNode;
  ariaLabel: string;
  color?: 'primary' | 'success';
  dataQaId?: string;
  grow?: FlexStyleProps['grow'];
  justifyContent?: FlexStyleProps['justifyContent'];
  toggleTextColor?: DesignSystemTextColor;
}

export const ToggleCheckbox: React.FC<ToggleCheckboxProps> = ({
  checked,
  onChange,
  className = '',
  toggleOnText = '',
  toggleOffText = '',
  isLocked = false,
  children = null,
  ariaLabel = 'ToggleCheckbox',
  color = 'primary',
  dataQaId = 'toggle-checkbox',
  grow = 0,
  justifyContent,
  toggleTextColor = 'default',
}) => {
  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isLocked) {
      onChange(event);
    }
  };

  const hasBothLabels = !!toggleOnText && !!toggleOffText;
  const toggleOffTextColor = (hasBothLabels && checked) || isLocked ? 'disabled' : toggleTextColor;
  const toggleOnTextColor = (hasBothLabels && !checked) || isLocked ? 'disabled' : toggleTextColor;

  return (
    <FlexDynamic
      className={className}
      w="full"
      direction="row"
      gap="1"
      alignItems="center"
      justifyContent={justifyContent ?? 'space-between'}
      grow={grow}
      data-testid="toggle-checkbox-wrapper"
      mdProps={{
        gap: '3',
        justifyContent: justifyContent ?? 'flex-end',
        direction: 'row',
        alignItems: 'center',
        w: 'auto',
      }}
    >
      {toggleOffText && (
        <BodyText fontWeight="medium" color={toggleOffTextColor} className="cn_select-none">
          {toggleOffText}
        </BodyText>
      )}
      <CheckboxWrapper data-testid="checkbox-wrapper">
        <CheckboxInput
          type="checkbox"
          checked={checked}
          onChange={handleToggleChange}
          name={ariaLabel}
          aria-label={ariaLabel}
          data-qa-id={dataQaId}
          disabled={isLocked}
        />
        <CheckboxSlider isLocked={isLocked} checked={checked} color={color} />
      </CheckboxWrapper>
      {toggleOnText && (
        <BodyText color={toggleOnTextColor} fontWeight="medium" className="cn_select-none">
          {toggleOnText}
        </BodyText>
      )}
      {children}
    </FlexDynamic>
  );
};
